import React, { useState, useEffect, useRef } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  validateMusicFile,
  extractYouTubeAudioId,
  validateMusicDuration,
  formatDuration,
} from "../utils/validation";
import ConfirmationModal from "../components/ConfirmationModal.jsx";

const mapServerTypeToFrontend = (serverType) => {
  if (!serverType) return "";

  const typeMap = {
    FILE: "upload",
    LINK: "url",
    YOUTUBE: "youtube",
  };

  // 서버 타입이 문자열인 경우와 enum 객체인 경우 모두 처리
  const typeString =
    typeof serverType === "string" ? serverType : serverType.toString();
  return typeMap[typeString] || typeString.toLowerCase();
};

const mapFrontendTypeToServer = (frontendType) => {
  const typeMap = {
    upload: "FILE",
    url: "LINK",
    youtube: "YOUTUBE",
  };
  return typeMap[frontendType] || frontendType;
};

// 개선된 바이너리 데이터를 URL로 변환하는 함수
const convertBinaryDataToUrl = (binaryData, contentType = "audio/mpeg") => {
  if (!binaryData) {
    return null;
  }

  try {
    let uint8Array;

    // 다양한 바이너리 데이터 형태 처리
    if (binaryData instanceof Uint8Array) {
      uint8Array = binaryData;
    } else if (binaryData instanceof ArrayBuffer) {
      uint8Array = new Uint8Array(binaryData);
    } else if (Array.isArray(binaryData)) {
      uint8Array = new Uint8Array(binaryData);
    } else if (typeof binaryData === "string") {
      // Base64 문자열인 경우 (서버에서 받은 데이터)
      try {
        const binaryString = atob(binaryData);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        uint8Array = bytes;
      } catch (e) {
        return null;
      }
    } else {
      return null;
    }

    if (uint8Array.length === 0) {
      return null;
    }

    // Blob 생성
    const blob = new Blob([uint8Array], { type: contentType });
    const objectUrl = URL.createObjectURL(blob);

    return objectUrl;
  } catch (error) {
    return null;
  }
};

// 파일 확장자에서 MIME 타입 추론
const getMimeTypeFromFilename = (fileName) => {
  if (!fileName) return "audio/mpeg";

  const ext = fileName.toLowerCase().split(".").pop();
  const mimeMap = {
    mp3: "audio/mpeg",
    wav: "audio/wav",
    ogg: "audio/ogg",
    m4a: "audio/mp4",
    aac: "audio/aac",
    flac: "audio/flac",
    wma: "audio/x-ms-wma",
  };

  return mimeMap[ext] || "audio/mpeg";
};

// 수정된 드래그 가능한 음악 아이템 컴포넌트 - UI 정렬 개선
const SortableMusicItem = ({
  mapping,
  index,
  onEdit,
  onRemove,
  onTest,
  isTestingMusic,
  testingIndex,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: mapping.id.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isCurrentlyTesting = isTestingMusic && testingIndex === index;

  // 서버 데이터 타입 변환 및 제목 우선순위 처리
  const bgmType = mapServerTypeToFrontend(
    mapping.bgm?.contentType || mapping.bgm?.type
  );
  const displayTitle = mapping.name || mapping.bgm?.title || "제목 없음";
  const fileName = mapping.bgm?.fileName || "파일";

  return (
    <div ref={setNodeRef} style={style} className="sortable-music-item">
      <div
        style={{
          background: "white",
          borderRadius: "8px",
          padding: "16px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          border: isDragging ? "2px dashed #00b894" : "1px solid #dee2e6",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flex: 1,
            minWidth: 0, // flex item이 축소될 수 있도록
          }}
        >
          {/* 아이콘 */}
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "8px",
              background: "linear-gradient(45deg, #fd79a8, #e84393)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
              flexShrink: 0,
            }}
          >
            {bgmType === "upload" && "📁"}
            {bgmType === "url" && "🔗"}
            {bgmType === "youtube" && "🎵"}
            {!bgmType && "🎵"}
          </div>

          {/* 음악 정보 - 왼쪽 정렬 및 텍스트 오버플로우 처리 */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              textAlign: "left",
            }}
          >
            <div
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#2d3436",
                marginBottom: "4px",
                textAlign: "left",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayTitle}
            </div>
            <div
              style={{
                fontSize: "13px",
                color: "#636e72",
                textAlign: "left",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {bgmType === "upload" &&
                `파일 업로드 | ${fileName} | ${formatDuration(
                  mapping.playDuration
                )}`}
              {bgmType === "url" &&
                `URL 링크 | ${formatDuration(mapping.playDuration)}`}
              {bgmType === "youtube" &&
                `YouTube | ${formatDuration(mapping.playDuration)}`}
              {!bgmType &&
                `재생시간: ${formatDuration(mapping.playDuration)} | 볼륨: ${
                  mapping.volume
                }%`}
            </div>
          </div>
        </div>

        {/* 버튼 그룹 */}
        <div
          style={{
            display: "flex",
            gap: "8px",
            flexShrink: 0,
            marginLeft: "12px",
          }}
        >
          <button
            style={{
              background: isCurrentlyTesting
                ? "linear-gradient(45deg, #e17055, #d63031)"
                : "linear-gradient(45deg, #fd79a8, #e84393)",
              color: "white",
              border: "none",
              borderRadius: "6px",
              padding: "6px 12px",
              fontSize: "12px",
              fontWeight: "500",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              onTest(mapping, index);
            }}
          >
            {isCurrentlyTesting ? "중지" : "테스트"}
          </button>
          <button
            style={{
              background: "linear-gradient(45deg, #74b9ff, #0984e3)",
              color: "white",
              border: "none",
              borderRadius: "6px",
              padding: "6px 12px",
              fontSize: "12px",
              fontWeight: "500",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(mapping, index);
            }}
          >
            수정
          </button>
          <button
            style={{
              background: "linear-gradient(45deg, #e17055, #d63031)",
              color: "white",
              border: "none",
              borderRadius: "6px",
              padding: "6px 12px",
              fontSize: "12px",
              fontWeight: "500",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              onRemove(mapping);
            }}
          >
            삭제
          </button>
        </div>
      </div>
    </div>
  );
};

// 수정된 MusicManagement - 서버 데이터 처리 개선
const MusicManagement = ({
  tvId,
  music,
  bgmSettings,
  onRefresh,
  onBgmSettingsChange,
  onMusicChange,
  toastSuccess,
  toastError,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedMusicType, setSelectedMusicType] = useState("");
  const [musicForm, setMusicForm] = useState({
    title: "",
    type: "",
    url: "",
    file: null,
    playDuration: "",
  });
  const [isUploading, setIsUploading] = useState(false);
  const [localMusicList, setLocalMusicList] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);

  // 삭제 확인 모달 상태 추가
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [musicToDelete, setMusicToDelete] = useState(null);

  // 파일 변경 관리
  const [currentFileInfo, setCurrentFileInfo] = useState(null);
  const [isFileChanged, setIsFileChanged] = useState(false);
  const [fileChangeMessage, setFileChangeMessage] = useState("");

  // 테스트 및 실시간 재생 상태 관리
  const [isTestingMusic, setIsTestingMusic] = useState(false);
  const [testingIndex, setTestingIndex] = useState(-1);
  const [testMusicPlayer, setTestMusicPlayer] = useState(null);
  const [testYouTubePlayer, setTestYouTubePlayer] = useState(null);
  const [isLiveMusicPlaying, setIsLiveMusicPlaying] = useState(false);
  const [liveMusicPlayer, setLiveMusicPlayer] = useState(null);
  const [liveMusicYouTubePlayer, setLiveMusicYouTubePlayer] = useState(null);

  // 서버 데이터 음악 URL 캐시 (메모리 관리 개선)
  const [serverMusicUrlCache, setServerMusicUrlCache] = useState(new Map());
  const urlCacheRef = useRef(new Set()); // 생성된 URL 추적

  // YouTube API 관리
  const [isYouTubeAPILoaded, setIsYouTubeAPILoaded] = useState(false);
  const youtubeCallbacksRef = useRef([]);
  const previewTimerRef = useRef(null);

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // YouTube API 로드 함수
  const loadYouTubeAPI = (callback) => {
    if (window.YT && window.YT.Player) {
      callback();
      return;
    }

    youtubeCallbacksRef.current.push(callback);

    if (document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      return;
    }

    const script = document.createElement("script");
    script.src = "https://www.youtube.com/iframe_api";

    window.onYouTubeIframeAPIReady = () => {
      setIsYouTubeAPILoaded(true);
      youtubeCallbacksRef.current.forEach((cb) => {
        try {
          cb();
        } catch (error) {}
      });
      youtubeCallbacksRef.current = [];
    };

    document.head.appendChild(script);
  };

  // 개선된 서버 데이터에서 음악 URL 처리
  const getServerMusicUrl = (bgmData) => {
    if (!bgmData) return null;

    const bgmType = mapServerTypeToFrontend(
      bgmData.contentType || bgmData.type
    );
    const cacheKey = `${bgmData.id}_${bgmType}_${
      bgmData.fileName || "unknown"
    }`;

    // 캐시에서 먼저 확인
    if (serverMusicUrlCache.has(cacheKey)) {
      return serverMusicUrlCache.get(cacheKey);
    }

    let musicUrl = null;

    if (bgmType === "upload" && bgmData.binaryData) {
      // 서버에서 받은 MIME 타입 우선 사용
      let mimeType = bgmData.mimeType || bgmData.mineType; // 서버 오타 고려

      if (!mimeType) {
        // MIME 타입이 없는 경우 파일명에서 추론
        mimeType = getMimeTypeFromFilename(bgmData.fileName);
      }

      musicUrl = convertBinaryDataToUrl(bgmData.binaryData, mimeType);

      if (musicUrl) {
        urlCacheRef.current.add(musicUrl);
      }
    } else if ((bgmType === "url" || bgmType === "youtube") && bgmData.url) {
      // URL 데이터 직접 사용
      musicUrl = bgmData.url;
    }

    // 캐시에 저장
    if (musicUrl) {
      setServerMusicUrlCache((prev) => new Map(prev.set(cacheKey, musicUrl)));
    }

    return musicUrl;
  };

  // music이 변경될 때 로컬 상태 업데이트 및 서버 데이터 URL 처리
  useEffect(() => {
    if (music) {
      const processedMusic = music.map((item) => {
        // 서버에서 받은 데이터인 경우 URL 처리
        if (item.bgm && !item.bgm.url && item.bgm.binaryData) {
          const processedUrl = getServerMusicUrl(item.bgm);
          return {
            ...item,
            bgm: {
              ...item.bgm,
              url: processedUrl,
              type: mapServerTypeToFrontend(item.bgm.contentType),
            },
          };
        }

        // 기존 데이터인 경우 타입만 변환
        if (item.bgm) {
          return {
            ...item,
            bgm: {
              ...item.bgm,
              type: mapServerTypeToFrontend(
                item.bgm.contentType || item.bgm.type
              ),
            },
          };
        }

        return item;
      });

      setLocalMusicList([...processedMusic]);
    }
  }, [music]);

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      stopAllMusic();
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
      }

      // 생성된 모든 Object URL 메모리 정리
      urlCacheRef.current.forEach((url) => {
        if (url && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
      urlCacheRef.current.clear();

      // 캐시된 URL도 정리
      serverMusicUrlCache.forEach((url) => {
        if (url && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  // 모달이 닫힐 때 실시간 재생 자동 중지
  useEffect(() => {
    if (!showAddModal) {
      stopLiveMusic();
    }
  }, [showAddModal]);

  // 개선된 모든 음악 재생 완전 중지 함수
  const stopAllMusic = () => {
    setIsTestingMusic(false);
    setTestingIndex(-1);
    setIsLiveMusicPlaying(false);

    // 테스트 음악 플레이어 완전 정리
    if (testMusicPlayer) {
      try {
        testMusicPlayer.pause();
        testMusicPlayer.currentTime = 0;
        testMusicPlayer.src = "";
        testMusicPlayer.load();

        // 모든 이벤트 리스너 제거
        testMusicPlayer.removeEventListener("loadeddata", () => {});
        testMusicPlayer.removeEventListener("canplaythrough", () => {});
        testMusicPlayer.removeEventListener("ended", () => {});
        testMusicPlayer.removeEventListener("error", () => {});
      } catch (error) {}
      setTestMusicPlayer(null);
    }

    // 실시간 음악 플레이어 완전 정리
    if (liveMusicPlayer) {
      try {
        liveMusicPlayer.pause();
        liveMusicPlayer.currentTime = 0;
        liveMusicPlayer.src = "";
        liveMusicPlayer.load();

        // 모든 이벤트 리스너 제거
        liveMusicPlayer.removeEventListener("loadeddata", () => {});
        liveMusicPlayer.removeEventListener("canplaythrough", () => {});
        liveMusicPlayer.removeEventListener("ended", () => {});
        liveMusicPlayer.removeEventListener("error", () => {});
      } catch (error) {}
      setLiveMusicPlayer(null);
    }

    // YouTube 테스트 플레이어 완전 정리
    if (testYouTubePlayer) {
      try {
        if (testYouTubePlayer.pauseVideo) {
          testYouTubePlayer.pauseVideo();
        }
        if (testYouTubePlayer.stopVideo) {
          testYouTubePlayer.stopVideo();
        }
        if (testYouTubePlayer.destroy) {
          testYouTubePlayer.destroy();
        }
      } catch (error) {}
      setTestYouTubePlayer(null);
    }

    // YouTube 실시간 플레이어 완전 정리
    if (liveMusicYouTubePlayer) {
      try {
        if (liveMusicYouTubePlayer.pauseVideo) {
          liveMusicYouTubePlayer.pauseVideo();
        }
        if (liveMusicYouTubePlayer.stopVideo) {
          liveMusicYouTubePlayer.stopVideo();
        }
        if (liveMusicYouTubePlayer.destroy) {
          liveMusicYouTubePlayer.destroy();
        }
      } catch (error) {}
      setLiveMusicYouTubePlayer(null);
    }

    // YouTube 컨테이너 DOM 완전 정리
    const testContainer = document.getElementById("youtubeTestContainer");
    if (testContainer) {
      testContainer.innerHTML = "";
      testContainer.remove();
    }

    const liveContainer = document.getElementById("liveMusicYouTubeContainer");
    if (liveContainer) {
      liveContainer.innerHTML = "";
      liveContainer.remove();
    }

    // 모든 숨겨진 오디오/비디오 엘리먼트 찾아서 정리
    const hiddenAudios = document.querySelectorAll(
      'audio[style*="position: absolute"]'
    );
    hiddenAudios.forEach((audio) => {
      try {
        audio.pause();
        audio.src = "";
        audio.remove();
      } catch (e) {}
    });

    const hiddenIframes = document.querySelectorAll(
      'iframe[id*="youtube-"], iframe[id*="music-"]'
    );
    hiddenIframes.forEach((iframe) => {
      try {
        iframe.remove();
      } catch (e) {}
    });
  };

  // 로컬 상태 변경을 부모 컴포넌트에 알림 (표시용 데이터만)
  const notifyMusicChange = (newMusicList) => {
    setLocalMusicList(newMusicList);
    if (onMusicChange && typeof onMusicChange === "function") {
      onMusicChange(newMusicList); // 브라우저 표시용 데이터만 전달
    }
  };

  const resetForm = () => {
    setMusicForm({
      title: "",
      type: "",
      url: "",
      file: null,
      playDuration: "",
    });
    setSelectedMusicType("");
    setIsEditMode(false);
    setEditingIndex(-1);
    setCurrentFileInfo(null);
    setIsFileChanged(false);
    setFileChangeMessage("");
  };

  const handleAddMusic = () => {
    setShowAddModal(true);
    resetForm();
  };

  const handleEditMusic = (mapping, index) => {
    const bgm = mapping.bgm;
    const bgmType = mapServerTypeToFrontend(bgm?.contentType || bgm?.type);

    setMusicForm({
      title: mapping.name || bgm?.title || "",
      type: bgmType,
      url: bgm?.url || "",
      file: null,
      playDuration: mapping.playDuration?.toString() || "",
    });
    setSelectedMusicType(bgmType);
    setIsEditMode(true);
    setEditingIndex(index);

    // 현재 파일 정보 설정 (upload 타입인 경우)
    if (bgmType === "upload") {
      setCurrentFileInfo({
        fileName: bgm.fileName || "알 수 없는 파일",
        title: mapping.name || bgm.title || "제목 없음",
      });
    }

    setIsFileChanged(false);
    setFileChangeMessage("");
    setShowAddModal(true);
  };

  // 수정된 음악 삭제 핸들러 - 모달 사용
  const handleRemoveMusic = (mapping) => {
    setMusicToDelete(mapping);
    setShowDeleteModal(true);
  };

  // 삭제 확인 핸들러
  const handleConfirmDelete = () => {
    if (!musicToDelete) return;

    const newMusicList = localMusicList.filter(
      (item) => item.id !== musicToDelete.id
    );
    notifyMusicChange(newMusicList);

    // 토스트 메시지 표시
    if (toastSuccess) {
      toastSuccess(
        `"${
          musicToDelete.name || musicToDelete.bgm?.title || "음악"
        }"이 삭제되었습니다.`
      );
    }

    setShowDeleteModal(false);
    setMusicToDelete(null);
  };

  // 삭제 취소 핸들러
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setMusicToDelete(null);
  };

  // 간소화된 개별 음악 테스트 함수 - 복잡한 로직 제거
  const handleTestMusic = async (mapping, index) => {
    // 현재 테스트 중인 음악이면 중지
    if (isTestingMusic && testingIndex === index) {
      stopAllMusic();
      return;
    }

    // 다른 음악이 재생 중이면 먼저 완전 중지
    stopAllMusic();
    await new Promise((resolve) => setTimeout(resolve, 100));

    setIsTestingMusic(true);
    setTestingIndex(index);

    const volume = (bgmSettings?.volume || 50) / 100;
    const bgm = mapping.bgm;

    if (!bgm) {
      if (toastError) {
        toastError("음악 데이터가 없습니다.");
      }
      setIsTestingMusic(false);
      setTestingIndex(-1);
      return;
    }

    try {
      // 업로드 파일인 경우 (바이너리 데이터 처리)
      if (bgm?.binaryData) {
        let audioData;

        // 바이너리 데이터를 Blob으로 직접 변환
        if (Array.isArray(bgm.binaryData)) {
          audioData = new Uint8Array(bgm.binaryData);
        } else if (typeof bgm.binaryData === "string") {
          // Base64인 경우
          try {
            const binaryString = atob(bgm.binaryData);
            audioData = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              audioData[i] = binaryString.charCodeAt(i);
            }
          } catch (e) {
            throw new Error("Base64 디코딩 실패: " + e.message);
          }
        } else {
          throw new Error(
            "지원하지 않는 바이너리 형식: " + typeof bgm.binaryData
          );
        }

        const mimeType = bgm.mimeType || bgm.mineType || "audio/mpeg";
        const blob = new Blob([audioData], { type: mimeType });
        const audioUrl = URL.createObjectURL(blob);

        const audio = new Audio(audioUrl);
        audio.volume = volume;

        audio.oncanplaythrough = () => {
          audio
            .play()
            .then(() => {
              setTestMusicPlayer(audio);
            })
            .catch((error) => {
              if (toastError) {
                // toastError("음악 재생에 실패했습니다: " + error.message);
              }
              setIsTestingMusic(false);
              setTestingIndex(-1);
              URL.revokeObjectURL(audioUrl);
            });
        };

        audio.onended = () => {
          setIsTestingMusic(false);
          setTestingIndex(-1);
          URL.revokeObjectURL(audioUrl); // 메모리 정리
        };

        audio.onerror = (e) => {
          if (toastError) {
            // toastError("음악 재생 실패");
          }
          setIsTestingMusic(false);
          setTestingIndex(-1);
          URL.revokeObjectURL(audioUrl); // 메모리 정리
        };
      } else if (bgm?.url) {
        // URL이 있는 경우

        // YouTube URL인지 확인
        const videoId = extractYouTubeAudioId(bgm.url);
        if (videoId) {
          loadYouTubeAPI(() => {
            createTestYouTubePlayer(videoId, volume);
          });
          return;
        }

        // 일반 URL 처리
        const audio = new Audio(bgm.url);
        audio.volume = volume;

        audio.oncanplaythrough = () => {
          audio
            .play()
            .then(() => {
              setTestMusicPlayer(audio);
            })
            .catch((error) => {
              if (toastError) {
                // toastError("URL에서 음악을 재생할 수 없습니다.");
              }
              setIsTestingMusic(false);
              setTestingIndex(-1);
            });
        };

        audio.onended = () => {
          setIsTestingMusic(false);
          setTestingIndex(-1);
        };

        audio.onerror = () => {
          if (toastError) {
            // toastError("URL 음악 재생 실패");
          }
          setIsTestingMusic(false);
          setTestingIndex(-1);
        };
      } else {
        if (toastError) {
          // toastError("재생할 수 있는 음악 데이터가 없습니다.");
        }
        setIsTestingMusic(false);
        setTestingIndex(-1);
      }
    } catch (error) {
      if (toastError) {
        // toastError("음악 테스트 실패: " + error.message);
      }
      setIsTestingMusic(false);
      setTestingIndex(-1);
    }
  };

  // 개선된 YouTube 테스트 플레이어 생성
  const createTestYouTubePlayer = (videoId, volume) => {
    // 기존 컨테이너 완전 정리
    let container = document.getElementById("youtubeTestContainer");
    if (container) {
      container.innerHTML = "";
      container.remove();
    }

    // 새 컨테이너 생성
    container = document.createElement("div");
    container.id = "youtubeTestContainer";
    container.style.cssText =
      "position: absolute; top: -1000px; left: -1000px; opacity: 0; pointer-events: none;";
    document.body.appendChild(container);

    // 고유한 플레이어 ID 생성 (중복 방지)
    const playerId = `youtube-test-player-${Date.now()}`;
    container.innerHTML = `<div id="${playerId}"></div>`;

    try {
      const player = new window.YT.Player(playerId, {
        height: "0",
        width: "0",
        videoId: videoId,
        playerVars: {
          autoplay: 1,
          loop: 0, // 테스트는 루프 비활성화
          controls: 0,
          disablekb: 1,
          fs: 0,
          modestbranding: 1,
          rel: 0,
          showinfo: 0,
          mute: 0,
          start: 0, // 처음부터 재생
        },
        events: {
          onReady: (event) => {
            try {
              event.target.setVolume(volume * 100);
              event.target.playVideo();
            } catch (error) {
              setIsTestingMusic(false);
              setTestingIndex(-1);
            }
          },
          onStateChange: (event) => {
            if (event.data === window.YT.PlayerState.PLAYING) {
              // 재생 시작
            } else if (event.data === window.YT.PlayerState.ENDED) {
              setIsTestingMusic(false);
              setTestingIndex(-1);
              // 재생 완료 후 플레이어 정리
              setTimeout(() => {
                try {
                  if (event.target && event.target.destroy) {
                    event.target.destroy();
                  }
                  const containerToRemove = document.getElementById(
                    "youtubeTestContainer"
                  );
                  if (containerToRemove) {
                    containerToRemove.remove();
                  }
                } catch (error) {
                  // 정리 오류 무시
                }
              }, 100);
            } else if (event.data === window.YT.PlayerState.PAUSED) {
              // 일시정지
            }
          },
          onError: (event) => {
            setIsTestingMusic(false);
            setTestingIndex(-1);

            let errorMessage = "YouTube 음악 재생 중 오류가 발생했습니다.";
            switch (event.data) {
              case 2:
                errorMessage = "잘못된 YouTube 비디오 ID입니다.";
                break;
              case 5:
                errorMessage = "YouTube 비디오를 재생할 수 없습니다.";
                break;
              case 100:
                errorMessage = "비디오를 찾을 수 없습니다.";
                break;
              case 101:
              case 150:
                errorMessage = "이 비디오는 임베드가 허용되지 않습니다.";
                break;
            }

            if (toastError) {
              toastError(errorMessage);
            }

            // 오류 발생 시 플레이어 정리
            setTimeout(() => {
              try {
                if (event.target && event.target.destroy) {
                  event.target.destroy();
                }
                const containerToRemove = document.getElementById(
                  "youtubeTestContainer"
                );
                if (containerToRemove) {
                  containerToRemove.remove();
                }
              } catch (error) {
                // 정리 오류 무시
              }
            }, 100);
          },
        },
      });

      setTestYouTubePlayer(player);
    } catch (error) {
      setIsTestingMusic(false);
      setTestingIndex(-1);
      if (toastError) {
        // toastError("YouTube 플레이어 생성에 실패했습니다.");
      }
    }
  };

  // 실시간 음악 재생 토글
  const toggleLiveMusicPreview = async () => {
    if (isLiveMusicPlaying) {
      stopLiveMusic();
      return;
    }

    if (!selectedMusicType) {
      if (toastError) {
        // toastError("음악 타입을 선택하세요.");
      }
      return;
    }

    const volume = (bgmSettings?.volume || 50) / 100;

    try {
      if (selectedMusicType === "upload") {
        const fileInput = document.getElementById("modalMusicFile");
        const base64Data = fileInput?.dataset?.base64;

        if (!base64Data) {
          if (toastError) {
            // toastError("먼저 음악 파일을 업로드하세요.");
          }
          return;
        }

        const audio = new Audio();
        audio.volume = volume;
        audio.loop = true;
        audio.preload = "auto";

        audio.addEventListener("loadeddata", () => {
          setIsLiveMusicPlaying(true);
        });

        audio.addEventListener("canplaythrough", () => {
          audio.play().catch((error) => {
            setIsLiveMusicPlaying(false);
            if (toastError) {
              // toastError("재생 오류가 발생했습니다.");
            }
          });
        });

        audio.addEventListener("ended", () => {
          setIsLiveMusicPlaying(false);
          setLiveMusicPlayer(null);
        });

        audio.addEventListener("error", (e) => {
          setIsLiveMusicPlaying(false);
          setLiveMusicPlayer(null);
          if (toastError) {
            // toastError("재생 오류가 발생했습니다.");
          }
        });

        audio.src = base64Data;
        setLiveMusicPlayer(audio);
      } else if (selectedMusicType === "url") {
        const url = musicForm.url.trim();

        if (!url) {
          if (toastError) {
            // toastError("음악 URL을 입력하세요.");
          }
          return;
        }

        const audio = new Audio();
        audio.volume = volume;
        audio.loop = true;
        audio.preload = "auto";

        audio.addEventListener("loadeddata", () => {
          setIsLiveMusicPlaying(true);
        });

        audio.addEventListener("canplaythrough", () => {
          audio.play().catch((error) => {
            setIsLiveMusicPlaying(false);
            setLiveMusicPlayer(null);
            if (toastError) {
              // toastError("URL에서 음악을 로드할 수 없습니다.");
            }
          });
        });

        audio.addEventListener("ended", () => {
          setIsLiveMusicPlaying(false);
          setLiveMusicPlayer(null);
        });

        audio.addEventListener("error", (e) => {
          setIsLiveMusicPlaying(false);
          setLiveMusicPlayer(null);
          if (toastError) {
            // toastError("URL에서 음악을 로드할 수 없습니다.");
          }
        });

        audio.src = url;
        setLiveMusicPlayer(audio);
      } else if (selectedMusicType === "youtube") {
        const url = musicForm.url.trim();

        if (!url) {
          if (toastError) {
            // toastError("YouTube URL을 입력하세요.");
          }
          return;
        }

        const videoId = extractYouTubeAudioId(url);
        if (!videoId) {
          if (toastError) {
            // toastError("유효한 YouTube URL을 입력하세요.");
          }
          return;
        }

        loadYouTubeAPI(() => {
          createLiveMusicYouTubePlayer(videoId, volume);
        });
      }
    } catch (error) {
      setIsLiveMusicPlaying(false);
      if (toastError) {
        // toastError("음악 재생 중 오류가 발생했습니다.");
      }
    }
  };

  // 개선된 YouTube 실시간 음악 플레이어 생성
  const createLiveMusicYouTubePlayer = (videoId, volume) => {
    // 기존 컨테이너 완전 정리
    let container = document.getElementById("liveMusicYouTubeContainer");
    if (container) {
      container.innerHTML = "";
      container.remove();
    }

    // 새 컨테이너 생성
    container = document.createElement("div");
    container.id = "liveMusicYouTubeContainer";
    container.style.cssText =
      "position: absolute; top: -1000px; left: -1000px; opacity: 0; pointer-events: none;";
    document.body.appendChild(container);

    // 고유한 플레이어 ID 생성 (중복 방지)
    const playerId = `live-music-youtube-player-${Date.now()}`;
    container.innerHTML = `<div id="${playerId}"></div>`;

    try {
      const player = new window.YT.Player(playerId, {
        height: "0",
        width: "0",
        videoId: videoId,
        playerVars: {
          autoplay: 1,
          loop: 1,
          playlist: videoId,
          controls: 0,
          disablekb: 1,
          fs: 0,
          modestbranding: 1,
          rel: 0,
          showinfo: 0,
          mute: 0,
        },
        events: {
          onReady: (event) => {
            try {
              event.target.setVolume(volume * 100);
              event.target.playVideo();
              setIsLiveMusicPlaying(true);
            } catch (error) {
              setIsLiveMusicPlaying(false);
            }
          },
          onStateChange: (event) => {
            if (event.data === window.YT.PlayerState.PLAYING) {
              setIsLiveMusicPlaying(true);
            } else if (event.data === window.YT.PlayerState.ENDED) {
              event.target.playVideo(); // 루프 재생
            } else if (event.data === window.YT.PlayerState.PAUSED) {
              // 일시정지
            }
          },
          onError: (event) => {
            setIsLiveMusicPlaying(false);

            let errorMessage = "YouTube 음악 재생 중 오류가 발생했습니다.";
            switch (event.data) {
              case 2:
                errorMessage = "잘못된 YouTube 비디오 ID입니다.";
                break;
              case 5:
                errorMessage = "YouTube 비디오를 재생할 수 없습니다.";
                break;
              case 100:
                errorMessage = "비디오를 찾을 수 없습니다.";
                break;
              case 101:
              case 150:
                errorMessage = "이 비디오는 임베드가 허용되지 않습니다.";
                break;
            }

            if (toastError) {
              // toastError(errorMessage);
            }

            // 오류 발생 시 플레이어 정리
            setTimeout(() => {
              try {
                if (event.target && event.target.destroy) {
                  event.target.destroy();
                }
                const containerToRemove = document.getElementById(
                  "liveMusicYouTubeContainer"
                );
                if (containerToRemove) {
                  containerToRemove.remove();
                }
              } catch (error) {
                // 정리 오류 무시
              }
            }, 100);
          },
        },
      });

      setLiveMusicYouTubePlayer(player);
    } catch (error) {
      setIsLiveMusicPlaying(false);
      if (toastError) {
        // toastError("YouTube 플레이어 생성에 실패했습니다.");
      }
    }
  };

  // 개선된 실시간 음악 재생 완전 중지
  const stopLiveMusic = () => {
    setIsLiveMusicPlaying(false);

    // 실시간 음악 플레이어 완전 정리
    if (liveMusicPlayer) {
      try {
        liveMusicPlayer.pause();
        liveMusicPlayer.currentTime = 0;
        liveMusicPlayer.src = "";
        liveMusicPlayer.load();

        // 모든 이벤트 리스너 제거
        liveMusicPlayer.removeEventListener("loadeddata", () => {});
        liveMusicPlayer.removeEventListener("canplaythrough", () => {});
        liveMusicPlayer.removeEventListener("ended", () => {});
        liveMusicPlayer.removeEventListener("error", () => {});
      } catch (error) {
        // 오류 무시
      }
      setLiveMusicPlayer(null);
    }

    // YouTube 실시간 플레이어 완전 정리
    if (liveMusicYouTubePlayer) {
      try {
        if (liveMusicYouTubePlayer.pauseVideo) {
          liveMusicYouTubePlayer.pauseVideo();
        }
        if (liveMusicYouTubePlayer.stopVideo) {
          liveMusicYouTubePlayer.stopVideo();
        }
        if (liveMusicYouTubePlayer.destroy) {
          liveMusicYouTubePlayer.destroy();
        }
      } catch (error) {
        // 오류 무시
      }
      setLiveMusicYouTubePlayer(null);
    }

    // YouTube 컨테이너 DOM 완전 정리
    const liveContainer = document.getElementById("liveMusicYouTubeContainer");
    if (liveContainer) {
      liveContainer.innerHTML = "";
      liveContainer.remove();
    }
  };

  const handleBgmSettingsChange = (newSettings) => {
    const updatedSettings = { ...bgmSettings, ...newSettings };

    if (onBgmSettingsChange && typeof onBgmSettingsChange === "function") {
      onBgmSettingsChange(updatedSettings);
    }
  };

  // 드래그 앤 드롭 핸들러
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const newMusicList = [...localMusicList];
      const oldIndex = newMusicList.findIndex(
        (item) => item.id.toString() === active.id
      );
      const newIndex = newMusicList.findIndex(
        (item) => item.id.toString() === over.id
      );

      const reorderedList = arrayMove(newMusicList, oldIndex, newIndex);
      notifyMusicChange(reorderedList);
    }
  };

  // 파일을 브라우저에 저장 및 바이너리 데이터 생성
  const handleFileUpload = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const arrayBuffer = event.target.result;
        const uint8Array = new Uint8Array(arrayBuffer);

        // base64 URL도 함께 생성 (미리보기용)
        const blob = new Blob([uint8Array], { type: file.type });
        const base64Url = URL.createObjectURL(blob);

        resolve({
          binaryData: Array.from(uint8Array), // 서버 전송용 바이너리 배열
          base64Url: base64Url, // 브라우저 재생용 URL
          fileName: file.name,
          fileType: file.type,
        });
      };
      reader.onerror = () => {
        reject(new Error("파일을 읽는 중 오류가 발생했습니다."));
      };
      reader.readAsArrayBuffer(file); // ArrayBuffer로 읽기
    });
  };

  // 수정된 함수: 브라우저 상태에만 추가/수정
  const handleSubmit = async () => {
    if (!selectedMusicType) {
      if (toastError) {
        toastError("음악 타입을 선택하세요.");
      }
      return;
    }

    if (!musicForm.title.trim()) {
      if (toastError) {
        toastError("제목을 입력하세요.");
      }
      return;
    }

    setIsUploading(true);

    try {
      let musicData = {
        title: musicForm.title.trim(),
        type: selectedMusicType,
        url: null,
        fileName: null,
      };

      // 타입별 처리
      if (selectedMusicType === "upload") {
        // 수정 모드에서 파일 변경이 있는 경우만 새로 처리
        if (isEditMode && !isFileChanged) {
          // 파일 변경이 없으면 기존 데이터 유지
          const originalMapping = localMusicList[editingIndex];
          musicData.url = originalMapping.bgm?.url;
          musicData.fileName = originalMapping.bgm?.fileName;
          musicData.binaryData = originalMapping.bgm?.binaryData;
        } else {
          // 새로 추가하거나 파일이 변경된 경우
          if (!musicForm.file) {
            if (toastError) {
              toastError("음악 파일을 선택하세요.");
            }
            return;
          }
          const validation = validateMusicFile(musicForm.file);
          if (!validation.valid) {
            if (toastError) {
              toastError(validation.message);
            }
            return;
          }

          // 파일을 브라우저와 서버 전송용으로 모두 처리
          const fileData = await handleFileUpload(musicForm.file);
          musicData.url = fileData.base64Url; // 브라우저 재생용
          musicData.fileName = fileData.fileName;
          musicData.binaryData = fileData.binaryData; // 서버 전송용 바이너리 데이터

          // 생성된 URL 추적
          urlCacheRef.current.add(fileData.base64Url);
        }
      } else if (selectedMusicType === "url") {
        if (!musicForm.url) {
          if (toastError) {
            toastError("음악 URL을 입력하세요.");
          }
          return;
        }
        musicData.url = musicForm.url;
        musicData.binaryData = null;
      } else if (selectedMusicType === "youtube") {
        if (!musicForm.url) {
          if (toastError) {
            toastError("YouTube URL을 입력하세요.");
          }
          return;
        }
        const audioId = extractYouTubeAudioId(musicForm.url);
        if (!audioId) {
          if (toastError) {
            toastError("유효한 YouTube URL을 입력하세요.");
          }
          return;
        }
        musicData.url = musicForm.url;
        musicData.binaryData = null;
      }

      const newMusicList = [...localMusicList];

      if (isEditMode) {
        const bgmMapping = {
          ...newMusicList[editingIndex],
          name: musicData.title,
          playDuration: musicData.playDuration,
          volume: bgmSettings?.volume || 50, // 개별 볼륨 유지
          bgm: {
            id: newMusicList[editingIndex].bgm?.id || null,
            title: musicData.title,
            type: musicData.type,
            contentType: mapFrontendTypeToServer(musicData.type), //서버 타입 추가
            url: musicData.url,
            fileName: musicData.fileName,
            binaryData: musicData.binaryData || null, // 바이너리 데이터 추가
          },
        };
        newMusicList[editingIndex] = bgmMapping;

        // 수정 성공 토스트
        if (toastSuccess) {
          toastSuccess(`"${musicData.title}" 음악이 수정되었습니다.`);
        }
      } else {
        const newBgmMapping = {
          id: Date.now() + Math.random(),
          mappingId: null,
          sortOrder: newMusicList.length + 1,
          name: musicData.title,
          playDuration: musicData.playDuration,
          volume: bgmSettings?.volume || 50, // 개별 볼륨 유지
          bgm: {
            id: null,
            title: musicData.title,
            type: musicData.type,
            contentType: mapFrontendTypeToServer(musicData.type), // 서버 타입 추가
            url: musicData.url,
            fileName: musicData.fileName,
            binaryData: musicData.binaryData || null, // 바이너리 데이터 추가
            isNewItem: true, // 새로 추가된 항목 표시
          },
        };
        newMusicList.push(newBgmMapping);

        // 추가 성공 토스트
        if (toastSuccess) {
          toastSuccess(`"${musicData.title}" 음악이 추가되었습니다.`);
        }
      }

      notifyMusicChange(newMusicList);

      setShowAddModal(false);
      resetForm();
      stopLiveMusic(); // 실시간 재생 중지
    } catch (error) {
      if (toastError) {
        toastError(
          `음악 ${isEditMode ? "수정" : "추가"} 실패: ${error.message}`
        );
      }
    } finally {
      setIsUploading(false);
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    resetForm();
    stopLiveMusic(); // 실시간 재생 중지
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
    }
  };

  const renderMusicForm = () => {
    if (!selectedMusicType) return null;

    return (
      <div>
        {/* 실시간 음악 재생 섹션 - 맨 위로 이동 */}
        <div style={{ marginBottom: "15px" }}>
          <div
            style={{
              background: "#f8f9fa",
              border: "1px solid #dee2e6",
              borderRadius: "8px",
              padding: "15px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                marginBottom: "10px",
                fontSize: "16px",
                fontWeight: "600",
              }}
            >
              🎵 실시간 음악 재생
            </div>
            <button
              type="button"
              onClick={toggleLiveMusicPreview}
              style={{
                background: isLiveMusicPlaying
                  ? "linear-gradient(45deg, #e17055, #d63031)"
                  : "linear-gradient(45deg, #00b894, #00cec9)",
                color: "white",
                border: "none",
                borderRadius: "6px",
                padding: "10px 20px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              {isLiveMusicPlaying ? "⏹️ 재생 중지" : "▶️ 재생 시작"}
            </button>
            <div style={{ fontSize: "12px", color: "#666", marginTop: "8px" }}>
              {isLiveMusicPlaying
                ? "음악이 재생 중입니다"
                : selectedMusicType === "upload" && isEditMode && !isFileChanged
                ? "새 파일을 선택하면 미리 들을 수 있습니다"
                : "클릭하면 입력한 음악을 미리 들을 수 있습니다"}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: "15px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "5px",
              fontWeight: "bold",
              color: "#2d3436",
            }}
          >
            제목
          </label>
          <input
            type="text"
            value={musicForm.title}
            onChange={(e) =>
              setMusicForm((prev) => ({ ...prev, title: e.target.value }))
            }
            placeholder="음악 제목 입력"
            style={{
              width: "100%",
              padding: "10px",
              border: "2px solid #ddd",
              borderRadius: "6px",
              fontSize: "14px",
            }}
          />
        </div>

        {selectedMusicType === "upload" && (
          <div style={{ marginBottom: "15px" }}>
            {/* 수정 모드일 때 현재 파일 정보 표시 */}
            {isEditMode && currentFileInfo && (
              <div style={{ marginBottom: "15px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                    color: "#2d3436",
                  }}
                >
                  현재 음악 파일
                </label>
                <div
                  style={{
                    background: "#f8f9fa",
                    border: "1px solid #dee2e6",
                    borderRadius: "6px",
                    padding: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <span style={{ fontSize: "18px" }}>📁</span>
                  <div>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#2d3436",
                      }}
                    >
                      {currentFileInfo.title}
                    </div>
                    <div style={{ fontSize: "12px", color: "#636e72" }}>
                      파일명: {currentFileInfo.fileName}
                    </div>
                  </div>
                </div>

                {/* 파일 변경 알림 */}
                {isFileChanged && (
                  <div
                    style={{
                      background: "#e3f2fd",
                      border: "1px solid #2196f3",
                      borderRadius: "6px",
                      padding: "10px",
                      marginTop: "10px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span style={{ fontSize: "16px" }}>✅</span>
                    <div style={{ fontSize: "14px", color: "#1976d2" }}>
                      <strong>파일이 변경되었습니다!</strong>
                      <br />
                      <span style={{ fontSize: "12px" }}>
                        {fileChangeMessage}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "bold",
                color: "#2d3436",
              }}
            >
              {isEditMode ? "변경할 음악 파일" : "음악 파일"}
            </label>
            <input
              id="modalMusicFile"
              type="file"
              accept="audio/*"
              onChange={(e) => {
                stopLiveMusic();

                const file = e.target.files[0];
                setMusicForm((prev) => ({ ...prev, file }));

                // 수정 모드에서 파일 변경 감지
                if (isEditMode) {
                  if (file) {
                    setIsFileChanged(true);
                    setFileChangeMessage(`새 파일: ${file.name}`);
                  } else {
                    setIsFileChanged(false);
                    setFileChangeMessage("");
                  }
                }

                if (file) {
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    const base64Data = event.target.result;
                    e.target.dataset.base64 = base64Data;
                  };
                  reader.onerror = () => {
                    if (toastError) {
                      toastError("파일을 읽는 중 오류가 발생했습니다.");
                    }
                  };
                  reader.readAsDataURL(file); // 미리보기는 base64로
                } else {
                  e.target.dataset.base64 = "";
                }
              }}
              style={{
                width: "100%",
                padding: "10px",
                border: "2px solid #ddd",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <small
              style={{
                color: "#666",
                fontSize: "12px",
                marginTop: "5px",
                display: "block",
              }}
            >
              지원 형식: MP3, WAV, OGG, AAC (최대 10MB)
              {isEditMode && (
                <span style={{ color: "#e17055", marginLeft: "10px" }}>
                  • 새 파일을 선택하지 않으면 기존 파일이 유지됩니다
                </span>
              )}
            </small>
          </div>
        )}

        {selectedMusicType === "url" && (
          <div style={{ marginBottom: "15px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "bold",
                color: "#2d3436",
              }}
            >
              음악 URL
            </label>
            <input
              type="url"
              value={musicForm.url}
              onChange={(e) => {
                stopLiveMusic();
                setMusicForm((prev) => ({ ...prev, url: e.target.value }));
              }}
              placeholder="https://example.com/music.mp3"
              style={{
                width: "100%",
                padding: "10px",
                border: "2px solid #ddd",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>
        )}

        {selectedMusicType === "youtube" && (
          <div style={{ marginBottom: "15px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "bold",
                color: "#2d3436",
              }}
            >
              YouTube URL
            </label>
            <input
              type="url"
              value={musicForm.url}
              onChange={(e) => {
                stopLiveMusic();
                setMusicForm((prev) => ({ ...prev, url: e.target.value }));
              }}
              placeholder="https://www.youtube.com/watch?v=... 또는 https://music.youtube.com/..."
              style={{
                width: "100%",
                padding: "10px",
                border: "2px solid #ddd",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <small
              style={{
                color: "#666",
                fontSize: "12px",
                marginTop: "5px",
                display: "block",
              }}
            >
              YouTube 또는 YouTube Music URL을 입력하세요
            </small>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      style={{
        background: "#f8f9fa",
        border: "2px dashed #dee2e6",
        borderRadius: "12px",
        padding: "20px",
        marginBottom: "20px",
      }}
    >
      {/* 섹션 헤더 */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          paddingBottom: "10px",
          borderBottom: "1px solid #dee2e6",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <span style={{ fontSize: "20px" }}>🎵</span>
          <h3
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: "600",
              color: "#2d3436",
            }}
          >
            배경 음악 관리
          </h3>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          {localMusicList && localMusicList.length >= 1 ? (
            <div
              style={{
                fontSize: "13px",
                color: "#e17055",
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
              }}
            >
              배경음악은 1개만 등록 가능합니다
            </div>
          ) : (
            <button
              onClick={handleAddMusic}
              style={{
                background: "linear-gradient(45deg, #00b894, #00cec9)",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "10px 16px",
                fontSize: "13px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              + 배경음악 추가
            </button>
          )}
        </div>
      </div>

      {/* 수정된 음악 설정 - 토글 스타일로 변경 */}
      <div
        style={{
          background: "white",
          borderRadius: "8px",
          padding: "16px",
          marginBottom: "15px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: "20px",
            alignItems: "center",
            marginBottom: "15px",
          }}
        >
          {/* 배경음악 사용 토글 */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontWeight: "bold", color: "#2d3436" }}>
              배경 음악 사용
            </span>
            <label
              style={{
                position: "relative",
                display: "inline-block",
                width: "50px",
                height: "26px",
              }}
            >
              <input
                type="checkbox"
                checked={bgmSettings?.enabled || false}
                onChange={(e) =>
                  handleBgmSettingsChange({ enabled: e.target.checked })
                }
                style={{
                  opacity: 0,
                  width: 0,
                  height: 0,
                }}
              />
              <span
                style={{
                  position: "absolute",
                  cursor: "pointer",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: bgmSettings?.enabled ? "#00b894" : "#ccc",
                  transition: "0.4s",
                  borderRadius: "26px",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    content: '""',
                    height: "20px",
                    width: "20px",
                    left: bgmSettings?.enabled ? "27px" : "3px",
                    bottom: "3px",
                    backgroundColor: "white",
                    transition: "0.4s",
                    borderRadius: "50%",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  }}
                />
              </span>
            </label>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              opacity: bgmSettings?.enabled ? 1 : 0.5,
            }}
          >
            <label
              style={{ fontSize: "14px", color: "#2d3436", fontWeight: "500" }}
            >
              볼륨:
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={bgmSettings?.volume || 50}
              onChange={(e) =>
                handleBgmSettingsChange({ volume: parseInt(e.target.value) })
              }
              style={{ flex: 1 }}
              disabled={!bgmSettings?.enabled}
            />
            <span
              style={{
                fontSize: "14px",
                color: "#636e72",
                width: "40px",
                fontWeight: "500",
              }}
            >
              {bgmSettings?.volume || 50}%
            </span>
          </div>
        </div>
      </div>

      {/* 음악 목록 - 드래그 앤 드롭 적용 */}
      {localMusicList && localMusicList.length > 0 ? (
        <div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={localMusicList.map((item) => item.id.toString())}
              strategy={verticalListSortingStrategy}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                {localMusicList.map((mapping, index) => (
                  <SortableMusicItem
                    key={mapping.id}
                    mapping={mapping}
                    index={index}
                    onEdit={handleEditMusic}
                    onRemove={handleRemoveMusic}
                    onTest={handleTestMusic}
                    isTestingMusic={isTestingMusic}
                    testingIndex={testingIndex}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      ) : (
        <div
          style={{
            background: "white",
            borderRadius: "8px",
            padding: "40px 20px",
            textAlign: "center",
            border: "2px dashed #dee2e6",
          }}
        >
          <div style={{ fontSize: "3em", marginBottom: "15px", opacity: 0.6 }}>
            🎵
          </div>
          <h4
            style={{
              margin: "0 0 8px 0",
              fontSize: "16px",
              color: "#495057",
              fontWeight: "600",
            }}
          >
            아직 추가된 배경 음악이 없습니다
          </h4>
          <p style={{ margin: 0, fontSize: "14px", color: "#6c757d" }}>
            위의 "음악 추가" 버튼을 클릭해서 시작하세요!
          </p>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        message={`"${
          musicToDelete?.name || musicToDelete?.bgm?.title || "음악"
        }"을 정말로 삭제하시겠습니까?`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isDelete={true}
      />

      {/* 음악 추가 모달 */}
      {showAddModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={closeModal}
        >
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "500px",
              width: "90%",
              maxHeight: "80vh",
              overflow: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
                paddingBottom: "10px",
                borderBottom: "1px solid #dee2e6",
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#2d3436",
                }}
              >
                {isEditMode ? "음악 수정" : "음악 추가"}
              </h2>
              <button
                onClick={closeModal}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#636e72",
                }}
              >
                &times;
              </button>
            </div>

            {!selectedMusicType ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: "15px",
                }}
              >
                <div
                  style={{
                    padding: "20px",
                    border: "2px solid #dee2e6",
                    borderRadius: "12px",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onClick={() => {
                    stopLiveMusic();
                    setSelectedMusicType("upload");
                  }}
                  onMouseOver={(e) => {
                    e.target.style.borderColor = "#00b894";
                    e.target.style.background = "#f8f9fa";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.borderColor = "#dee2e6";
                    e.target.style.background = "white";
                  }}
                >
                  <div style={{ fontSize: "32px", marginBottom: "8px" }}>
                    📁
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#2d3436",
                      marginBottom: "4px",
                    }}
                  >
                    파일 업로드
                  </div>
                  <div style={{ fontSize: "12px", color: "#636e72" }}>
                    MP3, WAV, OGG 등
                  </div>
                </div>
                <div
                  style={{
                    padding: "20px",
                    border: "2px solid #dee2e6",
                    borderRadius: "12px",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onClick={() => {
                    stopLiveMusic();
                    setSelectedMusicType("url");
                  }}
                  onMouseOver={(e) => {
                    e.target.style.borderColor = "#00b894";
                    e.target.style.background = "#f8f9fa";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.borderColor = "#dee2e6";
                    e.target.style.background = "white";
                  }}
                >
                  <div style={{ fontSize: "32px", marginBottom: "8px" }}>
                    🔗
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#2d3436",
                      marginBottom: "4px",
                    }}
                  >
                    URL 링크
                  </div>
                  <div style={{ fontSize: "12px", color: "#636e72" }}>
                    직접 링크
                  </div>
                </div>
                <div
                  style={{
                    padding: "20px",
                    border: "2px solid #dee2e6",
                    borderRadius: "12px",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onClick={() => {
                    stopLiveMusic();
                    setSelectedMusicType("youtube");
                  }}
                  onMouseOver={(e) => {
                    e.target.style.borderColor = "#00b894";
                    e.target.style.background = "#f8f9fa";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.borderColor = "#dee2e6";
                    e.target.style.background = "white";
                  }}
                >
                  <div style={{ fontSize: "32px", marginBottom: "8px" }}>
                    🎵
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#2d3436",
                      marginBottom: "4px",
                    }}
                  >
                    YouTube
                  </div>
                  <div style={{ fontSize: "12px", color: "#636e72" }}>
                    YouTube 음악
                  </div>
                </div>
              </div>
            ) : (
              <>
                {renderMusicForm()}
                <div
                  style={{
                    marginTop: "20px",
                    textAlign: "right",
                    display: "flex",
                    gap: "10px",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={() => setSelectedMusicType("")}
                    style={{
                      background: "#636e72",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      padding: "10px 16px",
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                  >
                    뒤로가기
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={isUploading}
                    style={{
                      background: "linear-gradient(45deg, #00b894, #00cec9)",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      padding: "10px 20px",
                      fontSize: "14px",
                      cursor: isUploading ? "wait" : "pointer",
                      opacity: isUploading ? 0.7 : 1,
                    }}
                  >
                    {isUploading ? "처리 중..." : isEditMode ? "수정" : "추가"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// 서버 전송용 데이터 변환 함수들을 외부에서 사용할 수 있도록 노출
MusicManagement.convertMusicListForServer = (musicList) => {
  return musicList.map((mapping, index) => {
    const bgm = mapping.bgm;

    // 새로운 아이템인지 확인하는 로직 강화
    const isNewItem =
      bgm?.isNewItem === true || // 명시적으로 새 아이템으로 표시된 경우
      !bgm?.id || // ID가 없는 경우
      (typeof bgm?.id === "string" && bgm.id.toString().includes("temp_")) || // 임시 ID인 경우
      !mapping.mappingId; // 매핑 ID가 없는 경우 (새로 추가된 경우)

    // BGM 요청 객체 생성
    const bgmRequest = {
      contentType: bgm?.contentType || mapFrontendTypeToServer(bgm?.type),
      sortOrder: index + 1,
      playDuration: mapping.playDuration,
      volume: mapping.volume || 50,
      binaryData: bgm?.binaryData || null,
      url: bgm?.type === "upload" ? null : bgm?.url, // 파일 업로드는 URL 제거

      // 수정: 새로운 아이템인 경우 existingBgmId를 null로 설정
      existingBgmId: isNewItem ? null : bgm?.id || null,
    };

    // 매핑 요청 객체 생성
    const mappingRequest = {
      mappingId: mapping.mappingId || null,
      sortOrder: index + 1,
      playDuration: mapping.playDuration,
      volume: mapping.volume || 50,
      name: mapping.name,
      bgmRequest: bgmRequest,
    };

    return mappingRequest;
  });
};

export default MusicManagement;
